import React from 'react';
import { useProductState } from '../store';

import { useTranslation } from 'react-i18next';
import Accordion from '@/components/v2/UI/Accordion';
import { SYMBOLS } from '@/helpers/symbolsMap';
import clsx from 'clsx';
import { filter, first, isEmpty, map } from 'lodash';
import Image from 'next/future/image';
import AwardReadMode from './AwardReadMode';

interface Props {
  containerClassName?: string;
}

const AccordionSection: React.FC<Props> = ({ containerClassName }) => {
  const { t } = useTranslation();
  const { product, variation } = useProductState();

  const _variation = variation || first(product.variations);

  return (
    <ul className={clsx('divide-y', containerClassName)}>
      {!isEmpty(product.awards) && (
        <Accordion
          as="li"
          name={t('P_PDP.OVERVIEW.ACCORDION.AWARDS')}
          defaultOpen
        >
          <ul className="gap-y-3">
            {product.awards.map(award => (
              <li className="grid grid-cols-6 gap-3" key={award.name}>
                {award.image && (
                  <div className="lg:col-span-1 col-span-2">
                    <Image
                      src={award.image}
                      alt={award.name}
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </div>
                )}
                <div
                  className={clsx(
                    'space-y-2',
                    award.image ? 'lg:col-span-5 col-span-4' : 'col-span-6'
                  )}
                >
                  <span className="text-left text-sm font-bold uppercase tracking-uyn">
                    {award.name}
                  </span>
                  <div className="lg:text-sm text-xs">
                    {award.description.length > 100 ? (
                      <>
                        {award.description.slice(0, 100) + '... '}
                        <AwardReadMode award={award} />
                      </>
                    ) : (
                      award.description
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Accordion>
      )}

      <Accordion
        as="li"
        name={t('P_PDP.OVERVIEW.ACCORDION.DESCRIPTION')}
        className="space-y-3"
      >
        <p>
          {product.description}
          <br />
          <br />
          {t('SELLING_CODE')}: {product.selling_code}
        </p>

        {variation && <p>{variation.sku}</p>}
      </Accordion>

      {!isEmpty(filter(product.bullet_list, Boolean)) && (
        <Accordion as="li" name={t('P_PDP.OVERVIEW.ACCORDION.FEATURES')}>
          {filter(product.bullet_list, Boolean).map((bullet, idx) => (
            <p
              key={idx}
              className="mb-2"
              dangerouslySetInnerHTML={{ __html: bullet }}
            />
          ))}
        </Accordion>
      )}

      {!isEmpty(_variation?.compositions) && (
        <Accordion
          as="li"
          name={t('P_PDP.OVERVIEW.ACCORDION.COMPOSITIONS')}
          className="space-y-3"
        >
          {map(
            _variation!.compositions,
            ({ compositionType, composition }, idx) => (
              <p key={idx} className="text-gray-700">
                {compositionType && <span>{compositionType}:</span>}

                {composition?.map((composition, idx) => (
                  <span key={idx} className="block">
                    {composition.percentage}% {composition.composition}
                  </span>
                ))}
              </p>
            )
          )}
        </Accordion>
      )}

      <Accordion as="li" name={t('P_PDP.OVERVIEW.ACCORDION.WASH_CARE')}>
        {product.productInfo.MoreInfo.map(({ name, items }) => (
          <ul key={name}>
            {items.map(({ istruction, symbol }) => (
              <li key={symbol}>
                <span className="font-Iconset">{SYMBOLS[symbol]}</span>
                <span> {istruction}</span>
              </li>
            ))}
          </ul>
        ))}
      </Accordion>
    </ul>
  );
};

export default AccordionSection;
